import type { MetricCatalogEntry, MetricCatalogGrouping, MetricCatalogTreeNode } from '@bindoc-projects/util-domain-lib';
import { metricCatalogTree } from '@bindoc-projects/util-domain-lib';

/**
 * BDA-6247 — the Cube's two Kennzahl catalogs, and the tree the picker builds
 * from them.
 *
 * `GET /operating-numbers/{virtual-clinic,fully-inpatient-case}-operating-numbers/available`
 * used to answer `id` and `displayName` and nothing else: no unit, no
 * grouping, no nesting. So the Cube's metric picker was an alphabetical list of
 * hundreds of names, and when BDA-6157 gave both cubes a tree dialog, the Cube
 * could only fill it with a flat one — there was no structure to show.
 *
 * The backend now declares one. Every entry carries a `section` and, where the
 * relationship exists, a `parentId`; the array itself arrives in render order
 * (by `section.order`, then declaration order, parents before children). So the
 * Cube's dialog has real sections — Ärztlicher Dienst, Pflegefachkräfte,
 * Erlöse & Casemix — and the rollups nest: `casemix-effektiv` over its four
 * years, `intuitive-op-gesamtkosten` over the eight cost components that make
 * it up.
 *
 * ## The unit is a map here and a string in the other cube
 *
 * `virtual-clinic-api` ships `unit` as `{de, en}`; `clinics-api` ships a plain
 * String for the same field. Reported on BDA-6247 — until the two agree, each
 * cube resolves its own shape at the wire edge, which is what
 * {@link aysOperatingNumberCatalogEntries} does here, and the shared tree
 * builder never learns which endpoint it was fed from.
 */

/** A `{de, en}` text as this API ships one. */
export type AysOperatingNumberText = Record<string, string>;

/** The heading an operating number sits under. */
export interface AysOperatingNumberSection {
  id: string;
  label: AysOperatingNumberText;
  /** Ascending, across the whole catalog — this API has no category level. */
  order?: number | null;
}

/**
 * One entry of an `available-operating-numbers` response.
 *
 * Every field past `id`/`displayName` is optional: they are additive
 * (BDA-6247), and a deployment that predates them answers the old two-field
 * shape. Everything here degrades to the flat list that shape produced.
 */
export interface AysOperatingNumberCatalogItem {
  id: string;
  displayName: string;
  /** `{de, en}`, unlike the Klinik-Cube's plain string — see the file docs. */
  unit?: AysOperatingNumberText | null;
  section?: AysOperatingNumberSection | null;
  /**
   * The operating number this one is a component of — e.g. each
   * `intuitive-op-*` cost under `intuitive-op-gesamtkosten`. The CHILD names
   * the parent.
   */
  parentId?: string | null;
}

/** One `{de, en}` text in the asked-for language, or null when there is none. */
export function aysOperatingNumberText(
  text: AysOperatingNumberText | null | undefined,
  lang: string,
): string | null {
  if (!text) return null;
  return text[lang] ?? text['en'] ?? null;
}

function grouping(
  section: AysOperatingNumberSection | null | undefined,
  lang: string,
): MetricCatalogGrouping | null {
  if (!section) return null;

  return {
    id: section.id,
    label: aysOperatingNumberText(section.label, lang) ?? section.id,
    order: section.order ?? null,
  };
}

/** The catalog as the shared tree builder consumes it, resolved to one language. */
export function aysOperatingNumberCatalogEntries(
  items: readonly AysOperatingNumberCatalogItem[],
  lang = 'de',
): MetricCatalogEntry[] {
  return items.map((item) => ({
    id: item.id,
    label: item.displayName,
    unit: aysOperatingNumberText(item.unit, lang),
    section: grouping(item.section, lang),
    parentId: item.parentId ?? null,
  }));
}

/**
 * The catalog as the picker's tree dialog renders it.
 *
 * No category level, because this API has none — see
 * {@link metricCatalogTree} on why that is derived rather than configured.
 */
export function aysOperatingNumberCatalogTree(
  items: readonly AysOperatingNumberCatalogItem[],
  lang = 'de',
): MetricCatalogTreeNode[] {
  return metricCatalogTree(aysOperatingNumberCatalogEntries(items, lang));
}
